import React from "react";
import CardTemplate from "components/card/card-template/CardTemplate";
import { useContactInformation } from "hooks/useContactInformation";
import { useQuote } from "hooks/useQuote";
import { getMltaAgreementTexts, getGIWLAgreementTexts } from "constants/agreementTexts";
import IllinoisNotice from "components/card/illinois-notice/IllinoisNotice";
import CaliforniaNotice from "components/card/california-notice/CaliforniaNotice";
import { useProductContext } from "context/ProductContext";

interface MLTAAgreementProps {
    className?: string;
    checked?: boolean;
    onChange?: (checked: boolean) => void;
    hasError?: boolean;
}

const MLTAAgreement = ({ className = "", checked = false, onChange, hasError = false }: MLTAAgreementProps) => {
    // ========== Hooks ==========
    const { contactData } = useContactInformation();
    const { quoteData } = useQuote();

    // ========== Computed Values ==========
    const { isGIWL } = useProductContext();
    const firstName = contactData?.contactInformation?.firstName || "[First Last]";
    const state = quoteData?.state || "";
    const agreementTexts = isGIWL ? getGIWLAgreementTexts(state) : getMltaAgreementTexts(state);
    const isIllinois = state === "IL";
    const isCaliforniaGIWL = isGIWL && state === "CA";

    // ========== Render ==========
    return (
        <>
            {isIllinois && <IllinoisNotice />}
            <CardTemplate title="" className={className} px="px-6 tablet:px-6">
                <div className="flex flex-col gap-4" id="agreement-content">
                    {isCaliforniaGIWL && <CaliforniaNotice />}
                    {/* Fraud Warning */}
                    <div className="flex flex-col gap-4 pt-6" id="agreement-fraud-warning">
                        <h3 className="font-sans font-bold text-lg leading-7 text-base-content">Fraud Warning</h3>
                        <p
                            className="text-base-content font-medium text-sm leading-5 font-sans"
                            dangerouslySetInnerHTML={{ __html: agreementTexts.fraudWarning }}
                        />
                    </div>

                    {/* Consent for Electronic Signature */}
                    <div className="flex flex-col gap-4" id="agreement-electronic-signature">
                        <h3 className="font-sans font-bold text-lg leading-7 text-base-content">
                            Consent for Electronic Signature
                        </h3>
                        <p
                            className="text-base-content font-medium text-sm leading-5 font-sans"
                            dangerouslySetInnerHTML={{ __html: agreementTexts.consentElectronicSignature }}
                        />
                    </div>

                    <div className="flex items-start gap-4 pt-2">
                        <input
                            type="checkbox"
                            name="mltaAgreementConsent"
                            id="mltaAgreementConsent"
                            className={`checkbox checkbox-md flex-shrink-0 mt-0.5 ${hasError ? "checkbox-error" : ""}`}
                            checked={checked}
                            onChange={(e) => onChange?.(e.target.checked)}
                            data-testid="mlta-agreement-consent-checkbox"
                            aria-describedby="agreement-fraud-warning agreement-electronic-signature"
                        />
                        <label
                            htmlFor="mltaAgreementConsent"
                            className="leading-5 text-sm text-base-content font-semibold"
                            data-testid="mlta-agreement-consent-label"
                        >
                            I, {firstName}, hereby certify that all the information I have provided in this application is
                            complete and accurate to the best of my knowledge. I understand the Fraud Warning and agree to the
                            Consent for Electronic Signature.
                        </label>
                    </div>
                    {hasError && (
                        <div
                            className="bg-warning border border-warning rounded-lg p-4"
                            data-testid="mlta-agreement-consent-error"
                        >
                            <p className="text-warning-content font-medium text-sm leading-5">
                                You must agree before continuing
                            </p>
                        </div>
                    )}
                </div>
            </CardTemplate>
        </>
    );
};

MLTAAgreement.displayName = "MLTAAgreement";

export default MLTAAgreement;
